import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
	AlertDialog,
	AlertDialogBody,
	AlertDialogContent,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogOverlay,
	Box,
	Button,
	Heading,
	HStack,
	Input,
	Text,
	useDisclosure,
	useToast,
	VStack,
} from '@bonusly/design-system';

import { useDisableBizyForCurrentUser } from '../../queries/bizy-member-enrollment';
import { useBizySidePanelStore } from '../bizy-side-panel';

const DISABLE_CONFIRMATION = 'disable bizy';

export const DisableBizySection = () => {
	const { t } = useTranslation('bizy_settings', { keyPrefix: 'disableBizy' });
	const [confirmation, setConfirmation] = useState('');
	const { mutate: disableBizy, isLoading } = useDisableBizyForCurrentUser();
	const closeBizyPanel = useBizySidePanelStore((state) => state.close);
	const toast = useToast();
	const confirmationDialog = useDisclosure();
	const cancelRef = useRef<HTMLButtonElement>(null);
	const isConfirmed = confirmation === DISABLE_CONFIRMATION;

	const handleDisable = () => {
		disableBizy(undefined, {
			onSuccess: () => {
				confirmationDialog.onClose();
				closeBizyPanel('user');
			},
			onError: () => toast({ title: t('error'), status: 'error', isClosable: true }),
		});
	};

	return (
		<>
			<HStack align="center" justify="space-between" spacing={4}>
				<Box pr={6}>
					<Heading as="h3" size="md" color="text.default">
						{t('title')}
					</Heading>
					<Text size="sm" color="text.softer" mt={1}>
						{t('description')}
					</Text>
				</Box>
				<Button size="md" variant="outline" colorScheme="secondary" flexShrink={0} onClick={confirmationDialog.onOpen}>
					{t('openConfirmation')}
				</Button>
			</HStack>

			<AlertDialog
				leastDestructiveRef={cancelRef}
				isOpen={confirmationDialog.isOpen}
				onClose={confirmationDialog.onClose}
				closeOnEsc={!isLoading}
				closeOnOverlayClick={!isLoading}
			>
				<AlertDialogOverlay>
					<AlertDialogContent>
						<AlertDialogHeader fontSize="lg" fontWeight="bold">
							{t('confirmationTitle')}
						</AlertDialogHeader>
						<AlertDialogBody>
							<VStack align="stretch" spacing={4}>
								<Text>{t('description')}</Text>
								<Box>
									<Text as="label" display="block" size="sm" variant="medium" color="text.default" mb={1}>
										{t('confirmationLabel', { confirmation: DISABLE_CONFIRMATION })}
									</Text>
									<Input
										aria-label={t('confirmationInputAriaLabel')}
										value={confirmation}
										onChange={(event) => setConfirmation(event.target.value)}
										placeholder={DISABLE_CONFIRMATION}
										isDisabled={isLoading}
									/>
								</Box>
							</VStack>
						</AlertDialogBody>
						<AlertDialogFooter>
							<Button ref={cancelRef} variant="alpha" colorScheme="secondary" onClick={confirmationDialog.onClose} isDisabled={isLoading}>
								{t('cancel')}
							</Button>
							<Button colorScheme="danger" ml={3} isDisabled={!isConfirmed || isLoading} isLoading={isLoading} onClick={handleDisable}>
								{t('action')}
							</Button>
						</AlertDialogFooter>
					</AlertDialogContent>
				</AlertDialogOverlay>
			</AlertDialog>
		</>
	);
};
